import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/auth-context'
import { Providers } from './providers'

// Layouts
import { AccountLayout } from './layouts/account-layout'
import { AuthLayout } from './layouts/auth-layout'
import { DashboardLayout } from './layouts/dashboard-layout'
import { SiteLayout } from './layouts/site-layout'

// Pages
import { AccountSettingsPage } from './pages/account'
import { DashboardPage } from './pages/dashboard'
import { InvitationAcceptPage } from './pages/invitations/accept'
import { LoginPage } from './pages/login'
import { OnboardingNewPage } from './pages/onboarding/new'
import { SiteDashboardPage } from './pages/site/dashboard'
import { PostMetadataPage } from './pages/site/editor/metadata'
import { PageEditorPage } from './pages/site/editor/page'
import { PostEditorPage } from './pages/site/editor/post'
import { SitePagesPage } from './pages/site/pages'
import { SitePostsPage } from './pages/site/posts'
import { SiteSettingsPage } from './pages/site/settings'
import { SiteSettingsAdvancedPage } from './pages/site/settings/advanced'
import { SiteSettingsApiDocsPage } from './pages/site/settings/api-docs'
import { SiteSettingsMcpPage } from './pages/site/settings/mcp'
import { SiteSettingsApiKeysPage } from './pages/site/settings/api-keys'
import { SiteSettingsDesignPage } from './pages/site/settings/design'
import { SiteSettingsDomainsPage } from './pages/site/settings/domains'
import { SiteSettingsImportPage } from './pages/site/settings/import'
import { SiteSettingsIntegrationsPage } from './pages/site/settings/integrations'
import { SiteSettingsNavPage } from './pages/site/settings/nav'
import { SiteSettingsNavItemPage } from './pages/site/settings/nav-item'
import { SiteSettingsStaffPage } from './pages/site/settings/staff'
import { SiteSettingsThemePage } from './pages/site/settings/theme'
import { SiteTagsPage } from './pages/site/tags'

interface ProtectedRouteProps {
  children: React.ReactNode
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()

  // Only block render when we have no cached user at all.
  // If a cached user exists, isAuthenticated is already true even while the
  // background verification is still in flight — so we can render immediately.
  if (isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export function App() {
  return (
    <BrowserRouter>
      <Providers>
        <AuthProvider>
          <Routes>
            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            <Route
              path="/invitations/:token"
              element={<InvitationAcceptPage />}
            />

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/onboarding/new" element={<OnboardingNewPage />} />
            </Route>

            {/* Account routes with sidebar */}
            <Route
              element={
                <ProtectedRoute>
                  <AccountLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/account" element={<AccountSettingsPage />} />
            </Route>

            {/* Site routes */}
            <Route
              path="/:siteId"
              element={
                <ProtectedRoute>
                  <SiteLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SiteDashboardPage />} />
              <Route path="posts" element={<SitePostsPage />} />
              <Route path="pages" element={<SitePagesPage />} />
              <Route path="tags" element={<SiteTagsPage />} />
              <Route path="settings" element={<SiteSettingsPage />} />
              <Route
                path="settings/staff"
                element={<SiteSettingsStaffPage />}
              />
              <Route
                path="settings/advanced"
                element={<SiteSettingsAdvancedPage />}
              />
              <Route
                path="settings/design"
                element={<SiteSettingsDesignPage />}
              />
              <Route
                path="settings/theme"
                element={<SiteSettingsThemePage />}
              />
              <Route
                path="settings/domains"
                element={<SiteSettingsDomainsPage />}
              />
              <Route
                path="settings/import"
                element={<SiteSettingsImportPage />}
              />
              <Route path="settings/nav" element={<SiteSettingsNavPage />} />
              <Route
                path="settings/nav/new"
                element={<SiteSettingsNavItemPage />}
              />
              <Route
                path="settings/nav/:id"
                element={<SiteSettingsNavItemPage />}
              />
              <Route
                path="settings/api-keys"
                element={<SiteSettingsApiKeysPage />}
              />
              <Route path="settings/mcp" element={<SiteSettingsMcpPage />} />
              <Route
                path="settings/api-docs"
                element={<SiteSettingsApiDocsPage />}
              />
              <Route
                path="settings/integrations"
                element={<SiteSettingsIntegrationsPage />}
              />
              <Route path="editor/post/new" element={<PostEditorPage />} />
              <Route path="editor/post/:id" element={<PostEditorPage />} />
              <Route
                path="editor/post/:id/metadata"
                element={<PostMetadataPage type="post" />}
              />
              <Route path="editor/page/new" element={<PageEditorPage />} />
              <Route path="editor/page/:id" element={<PageEditorPage />} />
              <Route
                path="editor/page/:id/metadata"
                element={<PostMetadataPage type="page" />}
              />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Providers>
    </BrowserRouter>
  )
}
